import { DEFAULT_ANIMATION_DURATION_MS } from '@/components/SpotlightTour/tourTypes'
import { scrollTo, tourTarget } from './stepFactory'
import type { TourGuide } from '@/components/SpotlightTour/tourTypes'

/**
 * Fractal Flame Discovery Tour
 *
 * An animated guided tour through the world of fractal flames.
 * Builds a visually striking composition step by step, showcasing
 * how transforms, variations, affines, and render settings work
 * together through the chaos game algorithm.
 *
 * Design goals:
 *  - Visual spectacle: each step creates a clear, dramatic change
 *  - Educational: explains what fractal flames are and how they work
 *  - Animated: parameters morph smoothly between states
 *  - Clean composition: strong affine geometry, not a mushy blob
 */

/** Grace period after the spotlight lands before animations begin. */
const GRACE_MS = 500

/** Longer duration for dramatic sweeping animations. */
const SLOW_MS = 1600

/** Standard animation duration. */
const MED_MS = DEFAULT_ANIMATION_DURATION_MS

export const flameCreationTour: TourGuide = {
  id: 'flame-creation',
  name: 'Fractal Flame Discovery',
  description:
    'An animated journey through the mathematics and beauty of fractal flames — from raw chaos to polished art.',
  noBlur: true,
  steps: [
    // -----------------------------------------------------------
    //  INTRODUCTION
    // -----------------------------------------------------------
    {
      target: tourTarget('canvas'),
      title: 'Fractal Flames',
      description:
        'Fractal flames are images generated by the chaos game — an algorithm that repeatedly applies random transformations to points. Millions of points land on the canvas, creating luminous, self-similar structures. Let us build one from scratch.',
      position: 'auto',
      beforeShow: (ctx) => {
        ctx.setSidebarOpen(false)
        ctx.executeCommand('flame.loadPreset', 'initExample')
        ctx.executeCommand('camera.center')
        ctx.executeCommand('camera.zoomTo', 1)
      },
    },

    // -----------------------------------------------------------
    //  RAW CHAOS -- What the algorithm sees
    // -----------------------------------------------------------
    {
      target: tourTarget('canvas'),
      title: 'Raw Chaos',
      description:
        'With Skip Iterations at 1 and a single linear transform, every point is visible — including the initial random scatter. This is the chaos game at its most basic: noise. The art comes from filtering this initial chaos.',
      beforeShow: (ctx) => {
        ctx.setSidebarOpen(true)
        ctx.executeCommand('flame.clearTransforms')
        ctx.executeCommand('flame.addTransform', 'linearVar')
        ctx.executeCommand('flame.setSkipIters', 1)
        ctx.executeCommand('flame.setExposure', 0.25)
        ctx.executeCommand('flame.setVibrancy', 0.5)
        ctx.executeCommand('flame.setGamma', 2.2)
        ctx.executeCommand('flame.setContrast', 1)
        ctx.executeCommand('camera.center')
        ctx.executeCommand('camera.zoomTo', 1)
      },
    },

    // -----------------------------------------------------------
    //  EMERGENCE -- Skip iterations reveal structure
    // -----------------------------------------------------------
    {
      target: tourTarget('skipIters-slider'),
      title: 'Emergence',
      description:
        'Skip Iterations discards early points. Watch the slider climb — structure emerges from chaos as the self-similar patterns of the fractal begin to dominate.',
      position: 'top',
      beforeShow: scrollTo('skipIters-slider'),
      animationDelay: GRACE_MS,
      onAnimate: (ctx) => {
        ctx.animateValue(1, 15, SLOW_MS, (val) => {
          ctx.executeCommand('flame.setSkipIters', Math.round(val))
        })
      },
    },

    // -----------------------------------------------------------
    //  TRANSFORM 1 -- Linear backbone with shaped affine
    // -----------------------------------------------------------
    {
      target: tourTarget('affine-editor'),
      title: 'T1: Shape the Backbone',
      description:
        'The pre-affine matrix positions and scales this transform in space. Shrinking it (a=0.8, e=0.6) and shifting it slightly right (c=0.2) creates the foundational shape that every iteration passes through.',
      beforeShow: scrollTo('affine-editor'),
      animationDelay: GRACE_MS,
      onAnimate: (ctx) => {
        ctx.animateValue(1, 0.8, SLOW_MS, (val) => {
          ctx.executeCommand('flame.setAffine', 0, 'pre', 'a', val)
        })
        ctx.animateValue(0, 0.2, SLOW_MS, (val) => {
          ctx.executeCommand('flame.setAffine', 0, 'pre', 'c', val)
        })
        ctx.animateValue(1, 0.6, SLOW_MS, (val) => {
          ctx.executeCommand('flame.setAffine', 0, 'pre', 'e', val)
        })
        ctx.executeCommand('flame.setAffine', 0, 'pre', 'b', 0)
        ctx.executeCommand('flame.setAffine', 0, 'pre', 'd', 0)
        ctx.executeCommand('flame.setAffine', 0, 'pre', 'f', 0)
        ctx.executeCommand('flame.setTransformColor', 0, 0.1, 0.25)
      },
    },

    // -----------------------------------------------------------
    //  TRANSFORM 2 -- Horseshoe for dramatic curl shapes
    // -----------------------------------------------------------
    {
      target: tourTarget('canvas'),
      title: 'T2: Add Horseshoe Curl',
      description:
        'The Horseshoe variation bends points into distinctive U-shaped curls. Combined with the linear backbone, this creates the classic fractal flame silhouette — sweeping arcs and dramatic curves.',
      beforeShow: (ctx) => {
        ctx.executeCommand('flame.addTransform', 'horseshoeVar')
        ctx.executeCommand('flame.setProbability', 0, 0.55)
        ctx.executeCommand('flame.setProbability', 1, 0.45)
        ctx.executeCommand('flame.setColorSpeed', 0, 0.4)
        ctx.executeCommand('flame.setColorSpeed', 1, 0.5)
        ctx.executeCommand('flame.setTransformColor', 1, 0.6, 0.3)
      },
    },
    {
      target: tourTarget('affine-editor'),
      title: 'T2: Shear & Rotate',
      description:
        'Shearing the pre-affine (b=0.3) tilts the horseshoe shape. A post-affine rotation then sweeps the curls into a dynamic fan. Watch the affine handles move as the shape transforms.',
      beforeShow: scrollTo('affine-editor'),
      animationDelay: GRACE_MS,
      onAnimate: (ctx) => {
        // Pre-affine: shear and scale
        ctx.animateValue(1, 0.7, SLOW_MS, (val) => {
          ctx.executeCommand('flame.setAffine', 1, 'pre', 'a', val)
        })
        ctx.animateValue(0, 0.3, SLOW_MS, (val) => {
          ctx.executeCommand('flame.setAffine', 1, 'pre', 'b', val)
        })
        ctx.animateValue(0, -0.3, SLOW_MS, (val) => {
          ctx.executeCommand('flame.setAffine', 1, 'pre', 'c', val)
        })
        ctx.executeCommand('flame.setAffine', 1, 'pre', 'd', 0)
        ctx.animateValue(1, 0.6, SLOW_MS, (val) => {
          ctx.executeCommand('flame.setAffine', 1, 'pre', 'e', val)
        })
        ctx.animateValue(0, 0.4, SLOW_MS, (val) => {
          ctx.executeCommand('flame.setAffine', 1, 'pre', 'f', val)
        })
        // Post-affine: 30-degree rotation
        const cos30 = Math.cos(Math.PI / 6)
        const sin30 = Math.sin(Math.PI / 6)
        ctx.executeCommand('flame.setAffine', 1, 'post', 'a', cos30)
        ctx.executeCommand('flame.setAffine', 1, 'post', 'b', -sin30)
        ctx.executeCommand('flame.setAffine', 1, 'post', 'd', sin30)
        ctx.executeCommand('flame.setAffine', 1, 'post', 'e', cos30)
      },
    },

    // -----------------------------------------------------------
    //  TRANSFORM 3 -- Swirl for spiral detail
    // -----------------------------------------------------------
    {
      target: tourTarget('canvas'),
      title: 'T3: Add Spiral Detail',
      description:
        'Swirl twists points around the origin, creating spiraling arms. At lower probability (20%), it adds delicate spiraling accents woven through the horseshoe structure — like wisps of smoke curling around the main shape.',
      beforeShow: (ctx) => {
        ctx.executeCommand('flame.addTransform', 'swirlVar')
        ctx.executeCommand('flame.setProbability', 0, 0.5)
        ctx.executeCommand('flame.setProbability', 1, 0.35)
        ctx.executeCommand('flame.setProbability', 2, 0.15)
        ctx.executeCommand('flame.setColorSpeed', 2, 0.6)
        ctx.executeCommand('flame.setTransformColor', 2, 0.85, 0.7)
      },
    },
    {
      target: tourTarget('affine-editor'),
      title: 'T3: Position & Scale',
      description:
        'Scaling the swirl down (a=0.5, e=0.5) and offsetting it (f=0.3) places the spirals at the top of the composition. The smaller scale makes the spirals delicate and fine-grained.',
      beforeShow: scrollTo('affine-editor'),
      animationDelay: GRACE_MS,
      onAnimate: (ctx) => {
        ctx.animateValue(1, 0.5, SLOW_MS, (val) => {
          ctx.executeCommand('flame.setAffine', 2, 'pre', 'a', val)
        })
        ctx.animateValue(0, 0.2, SLOW_MS, (val) => {
          ctx.executeCommand('flame.setAffine', 2, 'pre', 'b', val)
        })
        ctx.executeCommand('flame.setAffine', 2, 'pre', 'c', 0)
        ctx.executeCommand('flame.setAffine', 2, 'pre', 'd', 0)
        ctx.animateValue(1, 0.5, SLOW_MS, (val) => {
          ctx.executeCommand('flame.setAffine', 2, 'pre', 'e', val)
        })
        ctx.animateValue(0, 0.3, SLOW_MS, (val) => {
          ctx.executeCommand('flame.setAffine', 2, 'pre', 'f', val)
        })
      },
    },

    // -----------------------------------------------------------
    //  COLOR -- Palette exploration
    // -----------------------------------------------------------
    {
      target: tourTarget('canvas'),
      title: 'Color through the Palette',
      description:
        'Each transform carries a position in the color palette. Color Speed controls how quickly the palette shifts. Faster speeds create vivid color gradients along the fractal arms; slower speeds produce subtle, cohesive tones.',
      animationDelay: GRACE_MS,
      onAnimate: (ctx) => {
        // Animate color speeds for a color-shifting effect
        ctx.animateValue(0.4, 0.6, MED_MS, (val) => {
          ctx.executeCommand('flame.setColorSpeed', 0, val)
        })
        ctx.animateValue(0.5, 0.7, MED_MS, (val) => {
          ctx.executeCommand('flame.setColorSpeed', 1, val)
        })
        ctx.animateValue(0.6, 0.8, MED_MS, (val) => {
          ctx.executeCommand('flame.setColorSpeed', 2, val)
        })
        ctx.executeCommand('flame.setTransformColor', 0, 0.0, 0.4)
        ctx.executeCommand('flame.setTransformColor', 1, 0.5, 0.15)
        ctx.executeCommand('flame.setTransformColor', 2, 0.8, 0.9)
      },
    },

    // -----------------------------------------------------------
    //  RENDER POLISH -- Gamma, Vibrancy, Contrast
    // -----------------------------------------------------------
    {
      target: tourTarget('canvas'),
      title: 'Light and Shadow',
      description:
        'Render settings shape the final image. Gamma lifts the midtones to reveal detail. Vibrancy saturates colors. Contrast deepens the blacks between bright structures — together they transform a flat render into luminous art.',
      animationDelay: GRACE_MS,
      onAnimate: (ctx) => {
        ctx.animateValue(2.2, 2.6, MED_MS, (val) => {
          ctx.executeCommand('flame.setGamma', val)
        })
        ctx.animateValue(0.5, 0.85, MED_MS, (val) => {
          ctx.executeCommand('flame.setVibrancy', val)
        })
        ctx.animateValue(1, 1.3, MED_MS, (val) => {
          ctx.executeCommand('flame.setContrast', val)
        })
      },
    },

    // -----------------------------------------------------------
    //  TIMELINE ANIMATION
    // -----------------------------------------------------------
    {
      target: tourTarget('timeline-section'),
      title: 'Timeline Animation',
      description:
        'Finally, lets animate the probability of the Swirl transform over 90 frames. We will pulse it from its current 15% down to 0% and back. Notice how the spirals breathe in and out of the flame!',
      beforeShow: (ctx) => {
        ctx.setTimelineOpen(true)

        type SnapshotType = { transforms: Record<string, unknown> }
        const snapshot = ctx.snapshotFlame() as SnapshotType
        const tids = Object.keys(snapshot.transforms)
        if (tids.length > 2) {
          const tid = tids[2]
          const paramPath = `transform.${tid}.probability`

          ctx.executeCommand(
            'timeline.addKeyframe',
            paramPath,
            0.15,
            0,
            'easeInOut',
          )
          ctx.executeCommand(
            'timeline.addKeyframe',
            paramPath,
            0.0,
            45,
            'easeInOut',
          )
          ctx.executeCommand(
            'timeline.addKeyframe',
            paramPath,
            0.15,
            90,
            'easeInOut',
          )

          ctx.executeCommand('timeline.setDuration', 90)
          ctx.executeCommand('timeline.setFps', 15)
          ctx.executeCommand('timeline.setLoop', false)
          ctx.executeCommand('timeline.setAnimationEnabled', true)
          ctx.executeCommand('timeline.play')
        }
      },
    },

    // -----------------------------------------------------------
    //  FINAL -- Zoom and appreciate
    // -----------------------------------------------------------
    {
      target: tourTarget('canvas'),
      title: 'Your Fractal Universe',
      description:
        'Three transforms — Linear backbone, Horseshoe curls, and Swirl accents — each positioned and rotated in space by their affine matrices. You now know the core building blocks: transforms, variations, affine geometry, colors, and render settings. Every parameter is yours to explore.',
      animationDelay: GRACE_MS,
      onAnimate: (ctx) => {
        ctx.animateValue(1, 1.5, SLOW_MS, (val) => {
          ctx.executeCommand('camera.zoomTo', val)
        })
        ctx.executeCommand('camera.center')
      },
    },
  ],
  nextTourId: 'sidebar',
  nextTourLabel: 'Sidebar Deep Dive',
}
